import React from "react";
import { createPortal } from "react-dom";
import { Field, Form, Formik } from "formik";
import { z } from "zod";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { Dialog, DialogFooter } from "components/ui/Dialog";

import { useCreateDocumentType } from "../hooks/useCreateDocumentType";
import { useUpdateDocumentType } from "../hooks/useUpdateDocumentType";
import { ICON_OPTIONS } from "../tables/DocumentTypesTable/utils/constants";
import styles from "./DocumentTypeFormModal.module.css";

const documentTypeSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

const validateWithZod = (schema) => (values) => {
  try {
    schema.parse(values);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors.reduce((acc, err) => {
        const path = err.path.join(".");
        acc[path] = err.message;
        return acc;
      }, {});
    }
    return {};
  }
};

const STATUS_OPTIONS = [
  { value: "NEW", label: "New" },
  { value: "REFERRED", label: "Referred" },
  { value: "IN_REVIEW", label: "In Review" },
  { value: "REVIEWED", label: "Reviewed" },
  { value: "SYSTEM_GENERATED", label: "System Generated" },
];

export const DocumentTypeFormModal = ({
  isOpen,
  onClose,
  documentType,
  onSuccess,
}) => {
  const createMutation = useCreateDocumentType();
  const updateMutation = useUpdateDocumentType();

  const isEditMode = !!documentType;
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const normalizeIcon = (raw) => {
    if (!raw) return ICON_OPTIONS[0].value;
    const key = raw
      .split("-")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join("");
    return ICON_OPTIONS.find((o) => o.value === key) ? key : raw;
  };

  const initialValues = {
    name: documentType?.name || "",
    label: documentType?.label || "",
    icon: normalizeIcon(documentType?.icon),
    urgentReview: documentType?.urgentReview || false,
    createWorkflow: documentType?.createWorkflow || false,
    defaultNewStatus: documentType?.defaultNewStatus || "NEW",
    closeTaskOnReview: documentType?.closeTaskOnReview || false,
    disabled: documentType?.disabled || false,
  };

  const handleSubmit = (values) => {
    const payload = {
      name: values.name,
      label: values.label,
      icon: values.icon,
      urgentReview: values.urgentReview,
      createWorkflow: values.createWorkflow,
      defaultNewStatus: values.defaultNewStatus,
      closeTaskOnReview: values.closeTaskOnReview,
      disabled: values.disabled,
    };

    if (isEditMode) {
      payload.id = documentType.id;
    }

    const mutation = isEditMode ? updateMutation : createMutation;
    mutation.mutate(payload, {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Document Type" : "Add Document Type"}
      width={720}
    >
      <Formik
        initialValues={initialValues}
        validate={validateWithZod(documentTypeSchema)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, submitCount, values, setFieldValue }) => (
          <Form className={styles.form}>
            <div className={styles.formGrid}>
              <div className={styles.fieldGroup}>
                <label htmlFor="name" className={styles.label}>Name</label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  disabled={isEditMode}
                  placeholder="Enter document type name"
                  className={`${styles.input} ${
                    errors.name && (touched.name || submitCount > 0) ? styles.inputError : ""
                  } ${isEditMode ? styles.inputDisabled : ""}`}
                />
                {errors.name && (touched.name || submitCount > 0) && (
                  <span className={styles.error}>{errors.name}</span>
                )}
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="label" className={styles.label}>Label</label>
                <Field
                  type="text"
                  id="label"
                  name="label"
                  placeholder="Enter display label"
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>Icon</label>
                <IconSelect
                  value={values.icon}
                  onChange={(val) => setFieldValue("icon", val)}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="defaultNewStatus" className={styles.label}>Default Status</label>
                <Field as="select" id="defaultNewStatus" name="defaultNewStatus" className={styles.select}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Field>
              </div>

            </div>

            <div className={styles.checkboxList}>
              <label className={styles.checkboxLabel}>
                <Checkbox
                  checked={values.urgentReview}
                  onChange={(e) => setFieldValue("urgentReview", e.target.checked)}
                />
                Urgent Review
              </label>
              <label className={styles.checkboxLabel}>
                <Checkbox
                  checked={values.createWorkflow}
                  onChange={(e) => setFieldValue("createWorkflow", e.target.checked)}
                />
                Create Workflow
              </label>
              <label className={styles.checkboxLabel}>
                <Checkbox
                  checked={values.closeTaskOnReview}
                  onChange={(e) => setFieldValue("closeTaskOnReview", e.target.checked)}
                />
                Close Task on Review
              </label>
              <label className={styles.checkboxLabel}>
                <Checkbox
                  checked={values.disabled}
                  onChange={(e) => setFieldValue("disabled", e.target.checked)}
                />
                Disabled
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" size="large" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button variant="primary" size="large" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : isEditMode ? "Edit document type" : "Add new document type"}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const IconSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [dropdownStyle, setDropdownStyle] = React.useState({});
  const selectedOption = ICON_OPTIONS.find((opt) => opt.value === value);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={styles.iconSelectTrigger}
      >
        {selectedOption ? (
          <>
            <selectedOption.icon size={18} />
            <span>{selectedOption.label}</span>
          </>
        ) : (
          <span style={{ color: "#717186" }}>Select an icon</span>
        )}
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.iconSelectDropdown}
            style={dropdownStyle}
          >
            {ICON_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={styles.iconSelectOption}
                  style={{ background: value === opt.value ? "#f1f1f3" : "transparent" }}
                >
                  <Icon size={18} />
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};
